import React, { useContext, useEffect, useMemo, useState } from 'react';
import Brand from '../../../layout/Brand/Brand';
import Navigation, { NavigationLine } from '../../../layout/Navigation/Navigation';
import User from '../../../layout/User/User';
import {
	administrationAccount,
	administrationSite,
	appManagement,
	collectorMenu,
	dashboardMenu,
	documentsPlayer,
	crmModuleMenu,
	matchManagements,
	matchStatisticsManage,
	shopManagementUser,
	userTeam,
} from '../../../menu';
import ThemeContext from '../../../contexts/themeContext';
import Icon from '../../../components/icon/Icon';
import Aside, { AsideBody, AsideFoot, AsideHead } from '../../../layout/Aside/Aside';
import jwtInstance from '../../../guards/jwt.interceptor';
import { API_CRM } from '../../../utils/restApi';

interface CrmMe {
	hasAccess: boolean;
	canSeeTeams: boolean;
	principal: boolean;
}

const DefaultAside = () => {
	const user = JSON.parse(`${localStorage.getItem('user')}`);
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);
	const [doc, setDoc] = useState(false);
	const [crmMe, setCrmMe] = useState<CrmMe | null>(null);

	const canUseCrm =
		user?.roles[0] === 'ROLE_ADMIN' ||
		user?.roles[0] === 'ROLE_SUPER_ADMIN' ||
		user?.roles[0] === 'ROLE_OPERATOR';

	useEffect(() => {
		if (!user?.token || !canUseCrm) {
			return;
		}
		jwtInstance
			.get(API_CRM.me)
			.then((response) => setCrmMe(response.data?.hasAccess ? response.data : null))
			.catch(() => setCrmMe(null));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.token, canUseCrm]);

	// CRM Inscrieri sub-items by CRM role: operators only see Leaduri
	const crmMenu = useMemo(() => {
		if (!crmMe) {
			return null;
		}
		const { leads, signedTeams, reports, editions } = crmModuleMenu.crm.subMenu;
		return {
			crm: {
				...crmModuleMenu.crm,
				subMenu: {
					leads,
					...(crmMe.canSeeTeams ? { signedTeams, reports } : {}),
					...(crmMe.principal ? { editions } : {}),
				},
			},
		};
	}, [crmMe]);

	// Admins: CRM Inscrieri sits in "Administrare aplicatie", right under Marketplace
	const isAppAdmin = user?.roles[0] === 'ROLE_ADMIN' || user?.roles[0] === 'ROLE_SUPER_ADMIN';
	const appManagementMenu = useMemo(() => {
		if (!crmMenu) {
			return appManagement;
		}
		const merged: Record<string, any> = {};
		Object.entries(appManagement).forEach(([key, item]) => {
			merged[key] = item;
			if (key === 'marketplace') {
				merged.crm = crmMenu.crm;
			}
		});
		return merged as typeof appManagement;
	}, [crmMenu]);

	return (
		<>
			{user?.token && (
				<Aside>
					<AsideHead>
						<Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
					</AsideHead>
					<AsideBody>
						<>
							<Navigation menu={dashboardMenu} id='aside-dashboard' />

							{(user?.roles[0] === 'ROLE_PLAYER' ||
								user?.roles[0] === 'ROLE_DELEGATE') && (
								<Navigation menu={documentsPlayer} id='aside-documents-player' />
							)}

							{(user?.roles[0] === 'ROLE_ADMIN' ||
								user?.roles[0] === 'ROLE_SUPER_ADMIN' ||
								user?.roles[0] === 'ROLE_OBSERVER') && (
								<Navigation menu={matchManagements} id='match-management' />
							)}

							{(user?.roles[0] === 'ROLE_ADMIN' ||
								user?.roles[0] === 'ROLE_SUPER_ADMIN' ||
								user?.roles[0] === 'ROLE_ORGANIZER') && (
								<Navigation
									menu={matchStatisticsManage}
									id='match-statistics-management'
								/>
							)}

							{user?.roles[0] === 'ROLE_COLLECTOR' && (
								<Navigation menu={collectorMenu} id='aside-collector' />
							)}
						</>

						{!doc && (
							<>
								{isAppAdmin && (
									<Navigation menu={appManagementMenu} id='app-management' />
								)}
								{/* operators have no "Administrare aplicatie": CRM Inscrieri on its own */}
								{crmMenu && !isAppAdmin && <Navigation menu={crmMenu} id='crm' />}
								{user?.roles[0] === 'ROLE_MANAGER' && (
									<>
										<NavigationLine />
										<Navigation
											menu={administrationAccount}
											id='administration-account'
										/>
									</>
								)}
								<NavigationLine />
								{(user?.roles[0] === 'ROLE_MANAGER' ||
									user?.roles[0] === 'ROLE_DELEGATE' ||
									user?.roles[0] === 'ROLE_PLAYER') && (
									<Navigation menu={shopManagementUser} id='user-marketplace' />
								)}
								{user?.roles[0] === 'ROLE_MANAGER' && (
									<Navigation menu={userTeam} id='team-user' />
								)}

								{(user?.roles[0] === 'ROLE_ADMIN' ||
									user?.roles[0] === 'ROLE_SUPER_ADMIN' ||
									user?.roles[0] === 'ROLE_PHOTO_VIDEO') && (
									<>
										<Navigation menu={administrationSite} id='aside-menu' />
										<NavigationLine />
									</>
								)}
							</>
						)}
						{asideStatus && doc && <div className='p-4'>Documentation</div>}
					</AsideBody>
					<AsideFoot>
						<User isDefaultHeader={false} />
					</AsideFoot>
				</Aside>
			)}
		</>
	);
};

export default DefaultAside;
